import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { isBrowser } from '@/utils/browser';

interface PrivateHeaderContextProps {
  controlsTarget: HTMLDivElement | null;
  hideBackToLive: boolean;
  setControlsTarget: (element: HTMLDivElement | null) => void;
  setHideBackToLive: (value: boolean) => void;
}

const PrivateHeaderContext = createContext<PrivateHeaderContextProps>({
  controlsTarget: null,
  hideBackToLive: true,
  setControlsTarget: (_element) => undefined,
  setHideBackToLive: (_value) => undefined,
});

export function PrivateHeaderProvider({ children }: { children: ReactNode }) {
  const [controlsTarget, setControlsTarget] = useState<HTMLDivElement | null>(
    null,
  );
  const [hideBackToLive, setHideBackToLive] = useState(true);

  const value = useMemo(
    () => ({
      controlsTarget,
      hideBackToLive,
      setControlsTarget,
      setHideBackToLive,
    }),
    [controlsTarget, hideBackToLive],
  );

  return (
    <PrivateHeaderContext.Provider value={value}>
      {children}
    </PrivateHeaderContext.Provider>
  );
}

export function PrivateHeaderControlsOutlet() {
  const { setControlsTarget } = useContext(PrivateHeaderContext);
  const setTarget = useCallback(
    (element: HTMLDivElement | null) => setControlsTarget(element),
    [setControlsTarget],
  );

  return <div ref={setTarget} />;
}

export function PrivateHeaderControlsPortal({
  children,
  hideBackToLive = true,
}: {
  children?: ReactNode;
  hideBackToLive?: boolean;
}) {
  const { controlsTarget, setHideBackToLive } =
    useContext(PrivateHeaderContext);

  useEffect(() => {
    setHideBackToLive(hideBackToLive);
    return () => setHideBackToLive(true);
  }, [hideBackToLive, setHideBackToLive]);

  if (!children || !controlsTarget || !isBrowser()) {
    return null;
  }

  return createPortal(children, controlsTarget);
}

export function usePrivateHeader() {
  return useContext(PrivateHeaderContext);
}
